import {
  FaqFormDataInterface,
  EmptyInitialState,
} from '@src/apps/ContentEditor/Faq/FaqForm';
import { AnyAction } from 'redux';
import {
  FETCH_FAQ_CATEGORIES_SUCCESS,
  FETCH_FAQ_CATEGORIES_FAILURE,
  FETCH_FAQ_CATEGORIES_LOADING,
  SAVE_FAQ_FORM_SUCCESS,
  SAVE_FAQ_FORM_FAILURE,
  SAVE_FAQ_FORM_LOADING,
  FETCH_FAQ_LIST_DATA_SUCCESS,
  FETCH_FAQ_LIST_DATA_FAILURE,
  FETCH_FAQ_LIST_DATA_LOADING,
  DELETE_FAQ_SUCCESS,
  DELETE_FAQ_FAILURE,
  DELETE_FAQ_LOADING,
  UPDATE_FAQ_FORM_FIELD,
  UPDATE_FAQ_FORM_ERRORS,
  SET_FAQ_FORM_STATE,
  SET_IS_FAQ_FORM_SAVED,
  SET_FAQ_FORM_SAVED_ERROR,
  RESET_FAQ_FORM,
  EXPORT_FAQ_DATA_FAILURE,
  EXPORT_FAQ_DATA_LOADING,
  EXPORT_FAQ_DATA_SUCCESS,
  SET_FAQ_EDIT_DATA_SUCCESS,
  SET_FAQ_EDIT_DATA_FAILURE,
  SET_IS_FAQ_DELETED,
  SET_FAQ_EDIT_DATA_LOADING,
} from '@src/middleware/actions/actionTypes';

const emptyInitialState: EmptyInitialState = {
  faqFormData: {
    Id: '',
    QuestionId: 0,
    Question: '',
    Answer: '',
    CategorySource: '',
    CategorySourceId: '',
    MoreInfo: '',
    Notes: '',
    MetricAvailableIn: '',
    LastUpdated: '',
    LastEditedBy: '',
    UpdatedBy: '',
    UpdatedOn: '',
  },
  faqFormErrors: {
    Question: '',
    Answer: '',
    CategorySource: '',
    MoreInfo: '',
    Notes: '',
    MetricAvailableIn: '',
  },
};

interface FaqState {
  faqData: FaqFormDataInterface[];
  faqCategories: { CategorySourceId: string; CategorySource: string }[];
  faqQuestionMetrics: string[];
  faqFormData: FaqFormDataInterface;
  faqFormErrors: {
    Question: string;
    Answer: string;
    CategorySource: string;
    MoreInfo: string;
    Notes: string;
    MetricAvailableIn: string;
  };
  loading?: boolean;
  isSaving?: boolean;
  isDeleting?: boolean;
  faqCategoriesError?: string;
  isFaqFormSaved?: boolean;
  faqFormSavedError?: string;
  faqListFetchError?: string;
  faqFormState: 'create' | 'edit';
  isFaqDeletedSuccess?: boolean;
  isFaqDeletedFailure?: boolean;
  categoriesLoading?: boolean;
  listLoading?: boolean;
}

const initialState: FaqState = {
  faqData: [],
  faqCategories: [],
  faqQuestionMetrics: ['Internal (will apply to all metrics)', 'External'],
  faqFormData: {
    Id: '',
    QuestionId: 0,
    Question: '',
    Answer: '',
    CategorySource: '',
    CategorySourceId: '',
    MoreInfo: '',
    Notes: '',
    MetricAvailableIn: '',
    LastUpdated: '',
    LastEditedBy: '',
    UpdatedBy: '',
    UpdatedOn: '',
  },
  faqFormErrors: {
    Question: '',
    Answer: '',
    CategorySource: '',
    MoreInfo: '',
    Notes: '',
    MetricAvailableIn: '',
  },
  loading: false,
  isSaving: false,
  isDeleting: false,
  isFaqFormSaved: false,
  faqCategoriesError: '',
  faqFormSavedError: '',
  faqListFetchError: '',
  faqFormState: 'create',
  isFaqDeletedSuccess: false,
  isFaqDeletedFailure: false,
  categoriesLoading: false,
  listLoading: false,
};

const FaqReducer = (state = initialState, action: AnyAction): FaqState => {
  switch (action.type) {
    case UPDATE_FAQ_FORM_FIELD:
      return {
        ...state,
        faqFormData: {
          ...state.faqFormData,
          [action.payload.field]: action.payload.value,
        },
      };
    case UPDATE_FAQ_FORM_ERRORS:
      return {
        ...state,
        faqFormErrors: {
          ...state.faqFormErrors,
          ...action.payload,
        },
      };
    case FETCH_FAQ_CATEGORIES_SUCCESS:
      return {
        ...state,
        faqCategories: action.payload,
        loading: false,
        categoriesLoading: false,
      };
    case FETCH_FAQ_CATEGORIES_FAILURE:
      return {
        ...state,
        faqCategories: [],
        faqCategoriesError: action.payload,
        loading: false,
        categoriesLoading: false,
      };
    case FETCH_FAQ_CATEGORIES_LOADING:
      return {
        ...state,
        loading: action.payload,
        categoriesLoading: action.payload,
      };

    case SAVE_FAQ_FORM_SUCCESS:
      return {
        ...state,
        isSaving: false,
        loading: false,
        isFaqFormSaved: true,
      };
    case SAVE_FAQ_FORM_FAILURE:
      return {
        ...state,
        isSaving: false,
        loading: false,
        isFaqFormSaved: false,
        faqFormSavedError: action.payload,
      };
    case SAVE_FAQ_FORM_LOADING:
      return {
        ...state,
        loading: true,
        isSaving: true,
      };
    case FETCH_FAQ_LIST_DATA_SUCCESS:
      return {
        ...state,
        faqData: action.payload,
        loading: false,
        listLoading: false,
      };
    case FETCH_FAQ_LIST_DATA_FAILURE:
      return {
        ...state,
        faqData: [],
        faqListFetchError: action.payload,
        loading: false,
        listLoading: false,
      };
    case FETCH_FAQ_LIST_DATA_LOADING:
      return {
        ...state,
        loading: action.payload,
        listLoading: action.payload,
      };
    case SET_FAQ_FORM_STATE:
      return {
        ...state,
        faqFormState: action.payload,
        faqFormData:
          action.payload === 'create'
            ? emptyInitialState.faqFormData
            : state.faqFormData,
        faqFormErrors:
          action.payload === 'create'
            ? emptyInitialState.faqFormErrors
            : state.faqFormErrors,
      };
    case SET_IS_FAQ_FORM_SAVED:
      return {
        ...state,
        isFaqFormSaved: action.payload,
      };
    case SET_FAQ_FORM_SAVED_ERROR:
      return {
        ...state,
        faqFormSavedError: action.payload,
      };
    
    case DELETE_FAQ_SUCCESS:
      return {
        ...state,
        isDeleting: false,
        loading: false,
        isFaqDeletedSuccess: true,
        isFaqDeletedFailure: false,
      };
    case DELETE_FAQ_FAILURE:
      return {
        ...state,
        isDeleting: false,
        loading: false,
        isFaqDeletedSuccess: false,
        isFaqDeletedFailure: true,
      };
    case DELETE_FAQ_LOADING:
      return {
        ...state,
        loading: true,
        isDeleting: true,
      };
    case RESET_FAQ_FORM:
      return {
        ...state,
        faqFormData: initialState.faqFormData,
        faqFormErrors: initialState.faqFormErrors,
        isFaqFormSaved: initialState.isFaqFormSaved,
      };
    case SET_IS_FAQ_DELETED:
      return {
        ...state,
        isFaqDeletedSuccess: action.payload,
        isFaqDeletedFailure: false,
      };
    case EXPORT_FAQ_DATA_LOADING:
      return {
        ...state,
        loading: true,
      };
    case EXPORT_FAQ_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
      };
    case EXPORT_FAQ_DATA_FAILURE:
      return {
        ...state,
        loading: false,
      };
    case SET_FAQ_EDIT_DATA_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SET_FAQ_EDIT_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        faqFormData: action.payload,
      };
    case SET_FAQ_EDIT_DATA_FAILURE:
      return {
        ...state,
        loading: false,
        faqFormErrors: action.payload,
      };
    default:
      return state;
  }
};

export default FaqReducer;
